import { useEffect, useState } from "react";
import { challengeMfa, verifyMfa } from "@/hooks/useMfa";

interface MfaCodeFormProps {
  factorId: string;
  onSuccess: () => void;
  onBack: () => void;
}

/**
 * Giriş sırasında gösterilen 2FA kod doğrulama adımı.
 * Kayıtlı TOTP faktörü için challenge başlatır ve 6 haneli kodu doğrular.
 */
export default function MfaCodeForm({ factorId, onSuccess, onBack }: MfaCodeFormProps) {
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cid = await challengeMfa(factorId);
        if (active) setChallengeId(cid);
      } catch {
        if (active) setError("2FA doğrulaması başlatılamadı.");
      }
    })();
    return () => {
      active = false;
    };
  }, [factorId]);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await verifyMfa(factorId, challengeId, code.trim());
      onSuccess();
    } catch {
      setError("Kod hatalı. Lütfen tekrar dene.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-4">
      <div className="rounded-md bg-accent-100/70 px-4 py-3 text-sm text-accent-900">
        <i className="ri-shield-check-line mr-1" aria-hidden="true"></i>
        Hesabında iki faktörlü doğrulama aktif. Doğrulama uygulamasındaki 6 haneli kodu gir.
      </div>

      <div>
        <label htmlFor="mfa-code" className="block text-sm font-medium text-foreground-800">
          Doğrulama Kodu
        </label>
        <input
          id="mfa-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
          className="mt-1.5 w-full h-12 px-3.5 rounded-md border border-background-200 bg-background-100/60 text-center text-lg font-semibold tracking-[0.4em] text-foreground-900 placeholder:text-foreground-300 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors"
        />
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>
      )}

      <button
        type="submit"
        disabled={busy || code.length !== 6}
        className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? "Doğrulanıyor..." : "Doğrula ve Giriş Yap"}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-xs text-foreground-500 hover:text-foreground-700 transition-colors"
      >
        ← Geri dön
      </button>
    </form>
  );
}